import * as React from 'react';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  CompassIcon,
  Maximize2Icon,
  Minimize2Icon,
  RotateCcwIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from 'lucide-react';

import { DesktopTwinPanel } from '@/components/twin/desktop-twin-panel';
import { cn } from '@/lib/utils';

import { Button } from './button';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

export type PanDirection = 'up' | 'down' | 'left' | 'right';

interface ZoomPanControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onPan: (direction: PanDirection) => void;
  onReset: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  position?: 'bottom-right' | 'bottom-left';
  defaultMinimized?: boolean;
  minimizeOnMobile?: boolean;
  className?: string;
}

interface ControlButtonProps {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
}

function ControlButton({ label, children, onClick }: ControlButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-11 rounded-lg"
          aria-label={label}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="left" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function ZoomPanControls({
  onZoomIn,
  onZoomOut,
  onPan,
  onReset,
  onToggleFullscreen,
  isFullscreen = false,
  position = 'bottom-right',
  defaultMinimized = false,
  minimizeOnMobile = true,
  className,
}: ZoomPanControlsProps) {
  const positionClass = position === 'bottom-left' ? 'left-3 sm:left-4' : 'right-3 sm:right-4';

  const fullscreenControl = onToggleFullscreen ? (
    <ControlButton
      label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
      onClick={onToggleFullscreen}
    >
      {isFullscreen ? (
        <Minimize2Icon className="size-4" aria-hidden="true" />
      ) : (
        <Maximize2Icon className="size-4" aria-hidden="true" />
      )}
    </ControlButton>
  ) : null;

  return (
    <DesktopTwinPanel
      title="Navigate"
      icon={CompassIcon}
      className={cn('bottom-3 z-20 sm:bottom-4', positionClass, className)}
      panelClassName="w-[148px]"
      contentClassName="p-0"
      ariaLabel="Viewport navigation controls"
      collapsedActions={fullscreenControl}
      defaultMinimized={defaultMinimized}
      minimizeOnMobile={minimizeOnMobile}
      desktopOnly={false}
    >
      <div className="grid grid-cols-2 gap-1 border-y py-1" aria-label="Zoom controls" role="group">
        <ControlButton label="Zoom in" onClick={onZoomIn}>
          <ZoomInIcon className="size-4" aria-hidden="true" />
        </ControlButton>
        <ControlButton label="Zoom out" onClick={onZoomOut}>
          <ZoomOutIcon className="size-4" aria-hidden="true" />
        </ControlButton>
      </div>

      <div className="mt-1 grid grid-cols-3 place-items-center gap-1" aria-label="Pan controls" role="group">
        <span />
        <ControlButton label="Pan up" onClick={() => onPan('up')}>
          <ArrowUpIcon className="size-4" aria-hidden="true" />
        </ControlButton>
        <span />
        <ControlButton label="Pan left" onClick={() => onPan('left')}>
          <ArrowLeftIcon className="size-4" aria-hidden="true" />
        </ControlButton>
        <span className="flex size-11 items-center justify-center" aria-hidden="true">
          <CompassIcon className="size-4 text-muted-foreground" />
        </span>
        <ControlButton label="Pan right" onClick={() => onPan('right')}>
          <ArrowRightIcon className="size-4" aria-hidden="true" />
        </ControlButton>
        <span />
        <ControlButton label="Pan down" onClick={() => onPan('down')}>
          <ArrowDownIcon className="size-4" aria-hidden="true" />
        </ControlButton>
        <span />
      </div>

      <div
        className={cn('mt-1 grid gap-1 border-t pt-1', onToggleFullscreen ? 'grid-cols-2' : 'grid-cols-1')}
        aria-label="View controls"
        role="group"
      >
        <ControlButton label="Reset view" onClick={onReset}>
          <RotateCcwIcon className="size-4" aria-hidden="true" />
        </ControlButton>
        {fullscreenControl}
      </div>
    </DesktopTwinPanel>
  );
}
